import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactFlow, {
  Background, Controls, MiniMap, applyNodeChanges, applyEdgeChanges,
  addEdge, Handle, Position, ReactFlowProvider, useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Workflow, Plus, Trash2, Save, ArrowLeft, MessageCircle, HelpCircle,
  Link as LinkIcon, FileEdit, Calendar, BookOpen, GitBranch, Pencil, Play, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const STEP_TYPES = [
  { id: 'message', label: 'Message', icon: MessageCircle, color: '#3b82f6', bg: '#dbeafe' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: '#8b5cf6', bg: '#ede9fe' },
  { id: 'condition', label: 'Condition / Router', icon: GitBranch, color: '#f97316', bg: '#fed7aa' },
  { id: 'url', label: 'Open URL', icon: LinkIcon, color: '#06b6d4', bg: '#cffafe' },
  { id: 'form', label: 'Form', icon: FileEdit, color: '#10b981', bg: '#d1fae5' },
  { id: 'booking', label: 'Book Apt', icon: Calendar, color: '#ec4899', bg: '#fce7f3' },
  { id: 'article', label: 'Article', icon: BookOpen, color: '#f59e0b', bg: '#fef3c7' },
  { id: 'end', label: 'End', icon: GitBranch, color: '#64748b', bg: '#f1f5f9' },
];

const CONDITION_OPERATORS = [
  { id: 'equals', label: 'is' },
  { id: 'not_equals', label: 'is not' },
  { id: 'contains', label: 'contains' },
  { id: 'gt', label: '>' },
  { id: 'lt', label: '<' },
  { id: 'not_empty', label: 'is filled' },
  { id: 'empty', label: 'is empty' },
];

const stepMeta = (type) => STEP_TYPES.find(t => t.id === type) || STEP_TYPES[0];

// -------------------- Custom Node Component --------------------

const StepNode = ({ data, selected }) => {
  const meta = stepMeta(data.type);
  const Icon = meta.icon;
  const isQuestion = data.type === 'question';
  const isCondition = data.type === 'condition';

  return (
    <div
      className={`min-w-[220px] rounded-2xl border-2 bg-white shadow-sm transition-all ${selected ? 'shadow-lg ring-2 ring-violet-400' : 'hover:shadow-md'}`}
      style={{ borderColor: selected ? '#8b5cf6' : meta.color + '40' }}
    >
      {!data.isStart && (
        <Handle
          type="target"
          position={Position.Top}
          style={{ background: meta.color, width: 10, height: 10, border: '2px solid white' }}
        />
      )}

      <div className="flex items-center gap-2 px-3 py-2 rounded-t-2xl" style={{ background: meta.bg }}>
        <Icon className="h-4 w-4" style={{ color: meta.color }} />
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
        {data.isStart && (
          <span className="ml-auto text-[9px] font-bold px-1.5 py-0.5 rounded bg-white" style={{ color: meta.color }}>START</span>
        )}
      </div>

      <div className="px-3 py-2.5">
        <div className="text-sm font-semibold text-zinc-900 line-clamp-2 leading-snug">
          {data.title || data.content || (isQuestion ? 'Ask a question…' : isCondition ? 'If / else router' : 'Untitled step')}
        </div>
        {data.summary && (
          <div className="text-xs text-zinc-500 mt-1 line-clamp-1">{data.summary}</div>
        )}
        {isQuestion && data.options && data.options.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {data.options.map((o, i) => (
              <div key={i} className="text-[11px] text-zinc-600 flex items-center gap-1 truncate">
                <span className="text-zinc-400">•</span>{o.label || `Option ${i + 1}`}
              </div>
            ))}
          </div>
        )}
        {isCondition && data.conditions && data.conditions.length > 0 && (
          <div className="mt-2 space-y-0.5">
            {data.conditions.map((c, i) => (
              <div key={i} className="text-[11px] text-zinc-600 flex items-center gap-1 truncate">
                <span className="text-zinc-400">if</span>
                <span className="font-medium">{c.label || `${c.operator || 'equals'} ${c.value || '…'}`}</span>
              </div>
            ))}
            <div className="text-[11px] text-zinc-500 italic">else → fallback</div>
          </div>
        )}
      </div>

      {data.type === 'end' ? null : isQuestion ? (
        <div className="relative h-4">
          {(data.options || []).map((opt, i) => {
            const total = data.options.length;
            const pct = total === 1 ? 50 : (i / (total - 1)) * 80 + 10;
            return (
              <Handle
                key={i}
                id={`opt-${i}`}
                type="source"
                position={Position.Bottom}
                style={{ left: `${pct}%`, background: meta.color, width: 10, height: 10, border: '2px solid white' }}
              />
            );
          })}
        </div>
      ) : isCondition ? (
        <div className="relative h-4">
          {[...(data.conditions || []), { isElse: true }].map((c, i) => {
            const total = (data.conditions || []).length + 1;
            const pct = total === 1 ? 50 : (i / (total - 1)) * 80 + 10;
            return (
              <Handle
                key={i}
                id={c.isElse ? 'else' : `cond-${i}`}
                type="source"
                position={Position.Bottom}
                style={{ left: `${pct}%`, background: c.isElse ? '#64748b' : meta.color, width: 10, height: 10, border: '2px solid white' }}
              />
            );
          })}
        </div>
      ) : (
        <Handle
          type="source"
          position={Position.Bottom}
          style={{ background: meta.color, width: 10, height: 10, border: '2px solid white' }}
        />
      )}
    </div>
  );
};

const nodeTypes = { step: StepNode };

// -------------------- Conversion: flow schema ↔ react-flow --------------------

const flowToNodesEdges = (flow, forms = [], articles = []) => {
  const nodes = [];
  const edges = [];
  (flow.steps || []).forEach((step, i) => {
    const summary = (() => {
      if (step.type === 'url') return step.url || 'No URL set';
      if (step.type === 'form') {
        const f = forms.find(x => x.slug === step.form_slug);
        return f ? f.title : 'No form selected';
      }
      if (step.type === 'booking') return step.booking_url || 'No booking URL';
      if (step.type === 'article') {
        const a = articles.find(x => x.id === step.article_id);
        return a ? a.title : 'No article selected';
      }
      return null;
    })();
    nodes.push({
      id: step.id,
      type: 'step',
      position: step.position || { x: 80 + (i % 3) * 280, y: 60 + Math.floor(i / 3) * 200 },
      data: {
        type: step.type,
        title: step.content || step.title,
        content: step.content,
        summary,
        options: step.options,
        conditions: step.conditions,
        isStart: step.id === flow.start_step_id,
      },
    });

    // Edges for condition routes
    if (step.type === 'condition' && step.conditions) {
      step.conditions.forEach((c, ci) => {
        if (c.next_step_id) {
          edges.push({
            id: `e-${step.id}-cond${ci}-${c.next_step_id}`,
            source: step.id,
            sourceHandle: `cond-${ci}`,
            target: c.next_step_id,
            label: c.label || `${c.operator} ${c.value}`,
            type: 'smoothstep',
            style: { stroke: stepMeta('condition').color, strokeWidth: 2 },
            labelStyle: { fontSize: 11, fontWeight: 600, fill: '#374151' },
            labelBgPadding: [4, 2],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: '#fff', stroke: '#e5e7eb' },
          });
        }
      });
      if (step.else_next_step_id) {
        edges.push({
          id: `e-${step.id}-else-${step.else_next_step_id}`,
          source: step.id,
          sourceHandle: 'else',
          target: step.else_next_step_id,
          label: 'else',
          type: 'smoothstep',
          style: { stroke: '#64748b', strokeWidth: 2, strokeDasharray: '4 4' },
          labelStyle: { fontSize: 11, fontWeight: 600, fill: '#64748b' },
          labelBgStyle: { fill: '#fff', stroke: '#e5e7eb' },
        });
      }
    }
    // Edges for non-question/condition types
    // Edges for non-question/condition types
    if (step.type !== 'question' && step.type !== 'condition' && step.type !== 'end' && step.next_step_id) {
      edges.push({
        id: `e-${step.id}-${step.next_step_id}`,
        source: step.id,
        target: step.next_step_id,
        type: 'smoothstep',
        animated: false,
        style: { stroke: stepMeta(step.type).color, strokeWidth: 2 },
      });
    }
    // Edges for question options
    if (step.type === 'question' && step.options) {
      step.options.forEach((opt, oi) => {
        if (opt.next_step_id) {
          edges.push({
            id: `e-${step.id}-opt${oi}-${opt.next_step_id}`,
            source: step.id,
            sourceHandle: `opt-${oi}`,
            target: opt.next_step_id,
            label: opt.label,
            type: 'smoothstep',
            animated: false,
            style: { stroke: stepMeta('question').color, strokeWidth: 2 },
            labelStyle: { fontSize: 11, fontWeight: 600, fill: '#374151' },
            labelBgPadding: [4, 2],
            labelBgBorderRadius: 4,
            labelBgStyle: { fill: '#fff', stroke: '#e5e7eb' },
          });
        }
      });
    }
  });
  return { nodes, edges };
};

const nodesEdgesToFlow = (existingFlow, nodes, edges) => {
  const stepsById = Object.fromEntries((existingFlow.steps || []).map(s => [s.id, s]));
  const steps = nodes.map(n => {
    const existing = stepsById[n.id] || {};
    const s = { ...existing, id: n.id, type: n.data.type, position: n.position };
    if (n.data.type === 'question') {
      // Re-link options based on edges
      const opts = (existing.options || n.data.options || []).map((o, i) => {
        const matched = edges.find(e => e.source === n.id && e.sourceHandle === `opt-${i}`);
        return { ...o, next_step_id: matched?.target || null };
      });
      s.options = opts;
      s.next_step_id = null;
    } else if (n.data.type === 'condition') {
      const conds = (existing.conditions || n.data.conditions || []).map((c, i) => {
        const matched = edges.find(e => e.source === n.id && e.sourceHandle === `cond-${i}`);
        return { ...c, next_step_id: matched?.target || null };
      });
      s.conditions = conds;
      const elseEdge = edges.find(e => e.source === n.id && e.sourceHandle === 'else');
      s.else_next_step_id = elseEdge?.target || null;
      s.next_step_id = null;
    } else if (n.data.type === 'end') {
      s.next_step_id = null;
    } else {
      const matched = edges.find(e => e.source === n.id && !e.sourceHandle);
      s.next_step_id = matched?.target || null;
    }
    return s;
  });
  return { ...existingFlow, steps, start_step_id: existingFlow.start_step_id || nodes[0]?.id || null };
};

// -------------------- Editor Canvas --------------------

const FlowEditorCanvas = ({ flow, setFlow, forms, articles, onSave, onCancel, saving }) => {
  const initial = useMemo(() => flowToNodesEdges(flow, forms, articles), [flow.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [nodes, setNodes] = useState(initial.nodes);
  const [edges, setEdges] = useState(initial.edges);
  const [selectedNode, setSelectedNode] = useState(null);

  const onNodesChange = useCallback((changes) => setNodes(nds => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges(eds => applyEdgeChanges(changes, eds)), []);
  const onConnect = useCallback((conn) => {
    setEdges(eds => addEdge({ ...conn, type: 'smoothstep', style: { strokeWidth: 2, stroke: '#8b5cf6' } }, eds));
  }, []);

  const onNodeClick = (_, node) => setSelectedNode(node);

  const updateSelectedNode = (patch) => {
    setNodes(nds => nds.map(n => n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n));
    setSelectedNode(prev => prev ? { ...prev, data: { ...prev.data, ...patch } } : null);
  };

  const updateStepDeep = (patch) => {
    // Update both the underlying flow step and the visible node data
    const updated = { ...flow };
    updated.steps = updated.steps.map(s => s.id === selectedNode.id ? { ...s, ...patch } : s);
    setFlow(updated);
    const summary = (() => {
      if (patch.url !== undefined) return patch.url || 'No URL set';
      if (patch.form_slug !== undefined) {
        const f = forms.find(x => x.slug === patch.form_slug);
        return f ? f.title : 'No form selected';
      }
      if (patch.booking_url !== undefined) return patch.booking_url || 'No booking URL';
      if (patch.article_id !== undefined) {
        const a = articles.find(x => x.id === patch.article_id);
        return a ? a.title : 'No article selected';
      }
      return undefined;
    })();
    updateSelectedNode({
      ...(patch.content !== undefined ? { title: patch.content, content: patch.content } : {}),
      ...(patch.options !== undefined ? { options: patch.options } : {}),
      ...(patch.conditions !== undefined ? { conditions: patch.conditions } : {}),
      ...(summary !== undefined ? { summary } : {}),
    });
  };

  const addStep = (type) => {
    const id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const newStep = {
      id, type,
      content: type === 'message' ? 'New message' : (type === 'question' ? 'What can I help with?' : ''),
      next_step_id: null,
      position: { x: 100 + Math.random() * 400, y: 100 + Math.random() * 200 },
      ...(type === 'question' ? { options: [{ label: 'Option 1', next_step_id: null }] } : {}),
      ...(type === 'condition' ? { source_type: 'previous_answer', source_field: '', conditions: [{ label: 'Condition 1', operator: 'equals', value: '', next_step_id: null }], else_next_step_id: null } : {}),
      ...(type === 'url' ? { url: 'https://', button_label: 'Open' } : {}),
      ...(type === 'form' ? { form_slug: forms[0]?.slug || '' } : {}),
      ...(type === 'booking' ? { booking_url: '', button_label: 'Pick a time' } : {}),
      ...(type === 'article' ? { article_id: articles[0]?.id || '' } : {}),
      ...(type === 'end' ? { content: 'Thanks! Have a great day.' } : {}),
    };
    const updated = { ...flow, steps: [...flow.steps, newStep] };
    if (!updated.start_step_id) updated.start_step_id = id;
    setFlow(updated);
    const meta = stepMeta(type);
    setNodes(nds => [...nds, {
      id, type: 'step',
      position: newStep.position,
      data: { type, title: newStep.content, content: newStep.content, isStart: id === updated.start_step_id, options: newStep.options },
    }]);
    void meta;
  };

  const removeNode = (id) => {
    setNodes(nds => nds.filter(n => n.id !== id));
    setEdges(eds => eds.filter(e => e.source !== id && e.target !== id));
    setFlow({ ...flow, steps: flow.steps.filter(s => s.id !== id) });
    setSelectedNode(null);
  };

  const handleSave = () => {
    const finalFlow = nodesEdgesToFlow(flow, nodes, edges);
    onSave(finalFlow);
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50/60">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onCancel}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          <Input value={flow.name} onChange={e => setFlow({ ...flow, name: e.target.value })} className="h-9 max-w-xs font-semibold" data-testid="canvas-flow-name" />
          <Input value={flow.trigger_label} onChange={e => setFlow({ ...flow, trigger_label: e.target.value })} placeholder="Trigger label" className="h-9 max-w-[180px]" />
          <Input value={flow.trigger_icon || ''} onChange={e => setFlow({ ...flow, trigger_icon: e.target.value })} placeholder="💬" maxLength={4} className="h-9 w-16 text-center" />
          <Switch checked={flow.enabled !== false} onCheckedChange={v => setFlow({ ...flow, enabled: v })} />
          <span className="text-xs text-zinc-500">{flow.enabled !== false ? 'Live' : 'Disabled'}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 mr-2">{nodes.length} steps · {edges.length} connections</span>
          <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="canvas-save-button">
            <Save className="h-4 w-4 mr-1" />{saving ? 'Saving…' : 'Save Flow'}
          </Button>
        </div>
      </div>

      {/* Body: Sidebar palette + Canvas + Inspector */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left palette */}
        <div className="w-56 border-r border-zinc-200 bg-zinc-50/40 p-3 overflow-y-auto">
          <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Add a step</div>
          <div className="space-y-1.5">
            {STEP_TYPES.map(t => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => addStep(t.id)}
                  className="w-full flex items-center gap-2 p-2 rounded-lg border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/40 transition-all text-left"
                  data-testid={`palette-${t.id}`}
                >
                  <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ background: t.bg }}>
                    <Icon className="h-4 w-4" style={{ color: t.color }} />
                  </div>
                  <span className="text-sm font-medium text-zinc-700">{t.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900">
            <strong>💡 Tip:</strong> drag from the bottom dot of one step to the top of another to connect them. Question steps have one dot per option.
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            defaultEdgeOptions={{ type: 'smoothstep', style: { strokeWidth: 2, stroke: '#a78bfa' } }}
            data-testid="flow-canvas"
          >
            <Background gap={20} size={1} color="#e4e4e7" />
            <Controls />
            <MiniMap pannable zoomable nodeColor={(n) => stepMeta(n.data?.type).color} />
          </ReactFlow>
        </div>

        {/* Right inspector */}
        {selectedNode && (
          <div className="w-80 border-l border-zinc-200 bg-white p-4 overflow-y-auto" data-testid="step-inspector">
            <StepInspector
              node={selectedNode}
              step={flow.steps.find(s => s.id === selectedNode.id)}
              onChange={updateStepDeep}
              onRemove={() => removeNode(selectedNode.id)}
              forms={forms}
              articles={articles}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// -------------------- Step Inspector --------------------

const StepInspector = ({ node, step, onChange, onRemove, forms, articles }) => {
  if (!step) return <div className="text-sm text-zinc-500">Step not found.</div>;
  const meta = stepMeta(step.type);
  const Icon = meta.icon;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: meta.bg }}>
            <Icon className="h-4 w-4" style={{ color: meta.color }} />
          </div>
          <div className="font-bold text-zinc-900">{meta.label}</div>
        </div>
        <button onClick={onRemove} className="p-1.5 hover:bg-red-50 rounded text-red-500" data-testid="inspector-remove">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {(step.type === 'message' || step.type === 'end' || step.type === 'question') && (
        <div>
          <Label className="text-xs font-medium">{step.type === 'end' ? 'Closing message' : step.type === 'question' ? 'Question' : 'Message'}</Label>
          <Textarea value={step.content || ''} onChange={e => onChange({ content: e.target.value })} rows={3} className="mt-1" data-testid="inspector-content" />
        </div>
      )}

      {step.type === 'question' && (
        <div>
          <Label className="text-xs font-medium">Options (each routes to a different step)</Label>
          <div className="space-y-2 mt-1">
            {(step.options || []).map((opt, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-zinc-400 w-6">{i + 1}.</span>
                <Input value={opt.label} onChange={e => {
                  const options = [...step.options];
                  options[i] = { ...options[i], label: e.target.value };
                  onChange({ options });
                }} className="h-9" />
                <button onClick={() => {
                  const options = step.options.filter((_, idx) => idx !== i);
                  onChange({ options });
                }} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => {
            const options = [...(step.options || []), { label: `Option ${(step.options?.length || 0) + 1}`, next_step_id: null }];
            onChange({ options });
          }} className="mt-2 text-xs h-8" data-testid="inspector-add-option">
            <Plus className="h-3 w-3 mr-1" />Add option
          </Button>
          <p className="text-[11px] text-zinc-500 mt-2">Each option becomes an outgoing connection — drag from its dot to the target step.</p>
        </div>
      )}

      {step.type === 'url' && (
        <>
          <div>
            <Label className="text-xs font-medium">URL</Label>
            <Input value={step.url || ''} onChange={e => onChange({ url: e.target.value })} placeholder="https://…" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs font-medium">Button label</Label>
            <Input value={step.button_label || ''} onChange={e => onChange({ button_label: e.target.value })} placeholder="Open link" className="mt-1 h-9" />
          </div>
        </>
      )}

      {step.type === 'form' && (
        <div>
          <Label className="text-xs font-medium">Form</Label>
          <select value={step.form_slug || ''} onChange={e => onChange({ form_slug: e.target.value })} className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 bg-white text-sm">
            <option value="">— Pick a form —</option>
            {forms.map(f => <option key={f.id} value={f.slug}>{f.title}</option>)}
          </select>
        </div>
      )}

      {step.type === 'booking' && (
        <>
          <div>
            <Label className="text-xs font-medium">Booking URL</Label>
            <Input value={step.booking_url || ''} onChange={e => onChange({ booking_url: e.target.value })} placeholder="https://calendly.com/…" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs font-medium">Button label</Label>
            <Input value={step.button_label || ''} onChange={e => onChange({ button_label: e.target.value })} placeholder="Pick a time" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs font-medium">Description (optional)</Label>
            <Input value={step.content || ''} onChange={e => onChange({ content: e.target.value })} className="mt-1 h-9" />
          </div>
        </>
      )}

      {step.type === 'article' && (
        <div>
          <Label className="text-xs font-medium">Helpdesk article</Label>
          <select value={step.article_id || ''} onChange={e => onChange({ article_id: e.target.value })} className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 bg-white text-sm">
            <option value="">— Pick an article —</option>
            {articles.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
          </select>
        </div>
      )}

      {step.type === 'condition' && (
        <>
          <div>
            <Label className="text-xs font-medium">Branch on</Label>
            <select
              value={step.source_type || 'previous_answer'}
              onChange={e => onChange({ source_type: e.target.value })}
              className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 bg-white text-sm"
              data-testid="inspector-cond-source"
            >
              <option value="previous_answer">Previous question answer</option>
              <option value="form_field">Latest form field value</option>
              <option value="metadata">Visitor metadata (email/url/locale)</option>
            </select>
          </div>
          <div>
            <Label className="text-xs font-medium">Field / variable name (optional)</Label>
            <Input value={step.source_field || ''} onChange={e => onChange({ source_field: e.target.value })} placeholder="e.g. severity, email" className="mt-1 h-9" />
            <p className="text-[11px] text-zinc-500 mt-1">Leave blank to use the most recent visitor input.</p>
          </div>
          <div>
            <Label className="text-xs font-medium">Conditions (evaluated top-to-bottom)</Label>
            <div className="space-y-2 mt-1">
              {(step.conditions || []).map((c, i) => (
                <div key={i} className="p-2 border border-zinc-200 rounded-lg bg-zinc-50/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold text-zinc-400">IF</span>
                    <select
                      value={c.operator || 'equals'}
                      onChange={e => {
                        const conditions = [...step.conditions];
                        conditions[i] = { ...conditions[i], operator: e.target.value };
                        onChange({ conditions });
                      }}
                      className="h-8 px-1.5 rounded border border-zinc-200 bg-white text-xs"
                    >
                      {CONDITION_OPERATORS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                    {!['empty', 'not_empty'].includes(c.operator) && (
                      <Input
                        value={c.value || ''}
                        onChange={e => {
                          const conditions = [...step.conditions];
                          conditions[i] = { ...conditions[i], value: e.target.value };
                          onChange({ conditions });
                        }}
                        placeholder="value"
                        className="h-8 text-xs"
                      />
                    )}
                    <button onClick={() => {
                      const conditions = step.conditions.filter((_, j) => j !== i);
                      onChange({ conditions });
                    }} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <Input
                    value={c.label || ''}
                    onChange={e => {
                      const conditions = [...step.conditions];
                      conditions[i] = { ...conditions[i], label: e.target.value };
                      onChange({ conditions });
                    }}
                    placeholder="Display label (shown on the arrow)"
                    className="h-8 text-xs"
                  />
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => {
              const conditions = [...(step.conditions || []), { label: `Condition ${(step.conditions?.length || 0) + 1}`, operator: 'equals', value: '', next_step_id: null }];
              onChange({ conditions });
            }} className="mt-2 text-xs h-8" data-testid="inspector-add-condition">
              <Plus className="h-3 w-3 mr-1" />Add condition
            </Button>
            <p className="text-[11px] text-zinc-500 mt-2">Each condition gets its own output dot. The grey &quot;else&quot; dot is the fallback path when nothing matches.</p>
          </div>
        </>
      )}
    </div>
  );
};

// -------------------- Main Page (list of flows) --------------------

const ChatbotBuilder = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [flows, setFlows] = useState([]);
  const [forms, setForms] = useState([]);
  const [articles, setArticles] = useState([]);
  const [editingFlow, setEditingFlow] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [ws, fs, docs] = await Promise.all([
        axios.get(`${API_URL}/workspaces/my`),
        axios.get(`${API_URL}/forms`),
        axios.get(`${API_URL}/documents`),
      ]);
      setWorkspace(ws.data);
      setForms(fs.data.forms || []);
      setArticles(docs.data.documents || docs.data || []);
      setFlows(ws.data.settings?.chat_flows || []);
    } catch (e) {
      toast.error('Failed to load builder');
    }
  };

  const persist = async (nextFlows) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/workspaces/${workspace.id}/settings`, { chat_flows: nextFlows });
      setFlows(nextFlows);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const newFlow = () => ({
    id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: 'New Flow',
    trigger_label: 'Talk to us',
    trigger_icon: '💬',
    enabled: true,
    show_in_widget: true,
    steps: [{
      id: `s-${Date.now()}`,
      type: 'message',
      content: 'Hi! How can I help you today?',
      next_step_id: null,
      position: { x: 100, y: 100 },
    }],
    start_step_id: null,
  });

  const handleCreate = () => {
    const f = newFlow();
    f.start_step_id = f.steps[0].id;
    setEditingFlow(f);
  };

  const handleSaveFlow = async (finalFlow) => {
    if (!finalFlow.name) { toast.error('Flow name is required'); return; }
    if (!finalFlow.start_step_id) finalFlow.start_step_id = finalFlow.steps[0]?.id;
    const exists = flows.find(f => f.id === finalFlow.id);
    const next = exists ? flows.map(f => f.id === finalFlow.id ? finalFlow : f) : [...flows, finalFlow];
    await persist(next);
    setEditingFlow(null);
  };

  const handleDeleteFlow = async (id) => {
    if (!window.confirm('Delete this flow?')) return;
    await persist(flows.filter(f => f.id !== id));
  };

  const toggleFlowEnabled = async (id, enabled) => {
    await persist(flows.map(f => f.id === id ? { ...f, enabled } : f));
  };

  if (!workspace) return <Layout user={user} setUser={setUser}><div className="p-8">Loading…</div></Layout>;

  if (editingFlow) {
    return (
      <ReactFlowProvider>
        <FlowEditorCanvas
          flow={editingFlow}
          setFlow={setEditingFlow}
          forms={forms}
          articles={articles}
          onSave={handleSaveFlow}
          onCancel={() => setEditingFlow(null)}
          saving={saving}
        />
      </ReactFlowProvider>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="chatbot-builder-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Workflow className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Flow Builder
              </h1>
            </div>
            <p className="text-sm text-zinc-600">Visual canvas — drag steps, connect with arrows, branch with questions.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/chatbot-settings')}>
              <ArrowLeft className="h-4 w-4 mr-2" />Settings
            </Button>
            <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="new-flow-button">
              <Plus className="h-4 w-4 mr-2" />New Flow
            </Button>
          </div>
        </div>

        {flows.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-2xl bg-zinc-50/50">
            <Workflow className="h-12 w-12 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 mb-4">No flows yet — start your first canvas flow</p>
            <Button onClick={handleCreate} className="bg-violet-600 hover:bg-violet-700"><Plus className="h-4 w-4 mr-2" />Create Flow</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {flows.map(f => (
              <div key={f.id} className="bg-white border border-zinc-200 rounded-2xl p-5 hover:shadow-sm transition-shadow" data-testid={`flow-card-${f.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{f.trigger_icon || '💬'}</span>
                    <div>
                      <h3 className="font-bold text-zinc-900">{f.name}</h3>
                      <p className="text-xs text-zinc-500">{f.steps?.length || 0} steps · trigger: &quot;{f.trigger_label}&quot;</p>
                    </div>
                  </div>
                  <Switch checked={f.enabled !== false} onCheckedChange={v => toggleFlowEnabled(f.id, v)} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditingFlow(JSON.parse(JSON.stringify(f)))} className="flex-1" data-testid={`edit-flow-${f.id}`}>
                    <Pencil className="h-3 w-3 mr-1" />Open Canvas
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDeleteFlow(f.id)} className="px-2 text-red-500">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ChatbotBuilder;
